import type { ReactNode } from 'react';
import type { Dayjs } from 'dayjs';
import { AuthorName } from '@/components/AuditDate';
import { SortableHeader } from '@/components/table/SortableHeader';
import type { Sort } from '@/components/table/sort';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';

/** Une ligne d'historique de statut : quand, le statut (badge), par qui, et le motif éventuel (refus) */
export interface StatusHistoryLine {
  id: id;
  createdAt: Dayjs | undefined;
  createdBy: string | null | undefined;
  status: ReactNode;
  comment?: string | null;
}

/**
 * Historique de statut d'un objet (feuille d'heures, utilisateur) : Date (triable), Statut, Par, motif d'un refus. Les
 * lignes arrivent déjà triées et paginées par l'API (PagedStatusHistory) : sort / onSort pilotent l'en-tête.
 */
export function StatusHistoryTable({
  lines,
  sort,
  onSort,
  testId,
}: {
  lines: StatusHistoryLine[];
  sort: Sort<'createdAt'>;
  onSort: (sort: Sort<'createdAt'>) => void;
  testId?: string;
}) {
  return (
    <table className="w-full text-left text-sm" data-testid={testId}>
      <thead>
        <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <SortableHeader className="py-2" label="Date" sortKey="createdAt" sort={sort} onSort={onSort} />
          <th className="py-2">Statut</th>
          <th className="py-2">Par</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {lines.map((line) => (
          <tr key={line.id} className="align-top">
            <td className="py-3 pr-4 text-slate-600 whitespace-nowrap">{line.createdAt?.format(DATE_TIME_FORMAT)}</td>
            <td className="py-3 pr-4">
              {line.status}
              {line.comment && (
                <p className="mt-2 text-slate-700 whitespace-pre-line" data-testid="history-comment">
                  Motif : {line.comment}
                </p>
              )}
            </td>
            <td className="py-3 text-slate-600">
              <AuthorName by={line.createdBy ?? null} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
